import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../../components/MobileLayout';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface NotificationSettings {
  emailNotifications: boolean;
  marketingEmails: boolean;
}

const NotificationSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [settings, setSettings] = useState<NotificationSettings>({
    emailNotifications: true,
    marketingEmails: false,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/users/profile');
      setSettings({
        emailNotifications: response.data.emailNotifications ?? true,
        marketingEmails: response.data.marketingEmails ?? false,
      });
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    }
  };

  const handleToggle = async (key: keyof NotificationSettings) => {
    const newValue = !settings[key];
    const newSettings = { ...settings, [key]: newValue };
    setSettings(newSettings);

    try {
      await axios.put('/users/settings', { [key]: newValue });
      toast.success(t('settings.notifications.updateSuccess', 'Settings updated'));
    } catch (err: any) {
      // Revert on error
      setSettings(settings);
      toast.error(t('settings.notifications.updateError', 'Failed to update settings'));
    }
  };

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-light-border dark:border-dark-border">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/profile')}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-light-surface dark:hover:bg-dark-surface"
            >
              <ArrowLeftIcon className="w-5 h-5 text-light-text dark:text-dark-text" />
            </button>
            <h1 className="ml-4 text-xl font-semibold text-light-text dark:text-dark-text">
              {t('settings.notifications.title', 'Notifications')}
            </h1>
          </div>
        </div>

        {/* Settings */}
        <div className="flex-1 px-6 py-6 space-y-6">
          <div className="space-y-6">
            {/* Email Notifications */}
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <h3 className="text-light-text dark:text-dark-text font-medium">
                  {t('settings.notifications.emailNotifications', 'Email Notifications')}
                </h3>
                <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">
                  {t(
                    'settings.notifications.emailDescription',
                    'Receive notifications about your stories via email',
                  )}
                </p>
              </div>
              <button
                onClick={() => handleToggle('emailNotifications')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.emailNotifications
                    ? 'bg-primary-500'
                    : 'bg-light-border dark:bg-dark-border'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-[#ffffff] shadow transition-transform ${
                    settings.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Marketing Emails */}
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <h3 className="text-light-text dark:text-dark-text font-medium">
                  {t('settings.notifications.marketingEmails', 'Marketing Emails')}
                </h3>
                <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">
                  {t(
                    'settings.notifications.marketingDescription',
                    'Receive updates about new features and special offers',
                  )}
                </p>
              </div>
              <button
                onClick={() => handleToggle('marketingEmails')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.marketingEmails
                    ? 'bg-primary-500'
                    : 'bg-light-border dark:bg-dark-border'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-[#ffffff] shadow transition-transform ${
                    settings.marketingEmails ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default NotificationSettingsPage;
